import {
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2,
  PanelLeft,
  PanelLeftClose,
  Home,
  Users,
  UserRoundCog,
  Image,
  CreditCard,
  TrendingUp,
  Database,
  UtensilsCrossed,
  Dumbbell,
  BookOpen,
  ChevronUp,
  LogOut,
  UserRound,
  Radio,
  LifeBuoy,
  BarChart2,
  CalendarDays,
  QrCode,
  PhoneCall,
} from "lucide-react";
import flexlogo from "../../assets/Logo_transparent.png";
import { ApiContext } from "../../context/MyContext";
import {
  hasRouteAccess,
  normalizeEmployeeAccess,
} from "../../utils/accessControl";
import { DIET_ROUTES } from "../../features/diet/core/constants/routes";
import { WORKOUT_ROUTES } from "../../features/workouts/core/constants/routes";
import { HR_ROUTES } from "../../features/hr/core/constants/routes";
import { LINKS_ROUTES } from "../../features/links/core/constants/routes";
import { LEADS_ROUTES } from "../../features/leads/core/constants/routes";
import ProfileCard from "../ProfileCard/ProfileCard";
import NotificationBell from "../notifications/NotificationBell";
import { useRoomsUnread } from "../../hooks/comms/RoomsUnreadProvider";
import { useOversightAccess } from "../../hooks/comms/OversightAccessProvider";

const LAST_ACTIVE_KEY = "sidebar_last_active_id";

const NAV_SECTIONS = [
  {
    id: "primary",
    items: [
      { id: "home", label: "Home", Icon: Home, route: "/home" },
      // Chat now lives inside the Users "My users" split view rather than its
      // own destination, so the unread pill rides on this item.
      { id: "user", label: "Users", Icon: Users, route: "/user", badge: "unread" },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    items: [
      {
        id: "bookings",
        label: "Bookings",
        Icon: CalendarDays,
        route: "/bookings",
      },
    ],
  },
  {
    // Sales gets Leads without QR & Links; Marketing and Executive see it
    // too (features/leads/core/access, A9).
    id: "sales",
    label: "Sales",
    items: [
      { id: "leads", label: "Leads", Icon: PhoneCall, route: LEADS_ROUTES.LIST },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    items: [
      {
        id: "broadcasts",
        label: "Broadcasts",
        Icon: Radio,
        route: "/broadcasts",
      },
      {
        id: "plan-images",
        label: "Banners",
        Icon: Image,
        route: "/plan-images",
      },
      { id: "links", label: "QR & Links", Icon: QrCode, route: LINKS_ROUTES.LIST },
    ],
  },
  {
    id: "support",
    label: "Support",
    items: [
      // One entry, because there is now one screen: the support workspace
      // serves agents and leads alike and opens on the view that fits the
      // caller (their own tickets for an agent, the whole desk for a lead).
      // It used to be two — a staff-only dashboard and "My Tickets".
      {
        id: "support",
        label: "Support",
        Icon: LifeBuoy,
        route: "/support",
        badge: "supportUnread",
      },
      {
        id: "coaches",
        label: "Coaches",
        Icon: BarChart2,
        route: "/coaches",
        staffOnly: true,
      },
    ],
  },
  {
    id: "nutrition",
    label: "Nutrition",
    items: [
      {
        id: "diet-recipes",
        label: "Recipes",
        Icon: Database,
        route: DIET_ROUTES.RECIPES_LIST,
      },
      {
        id: "diet-templates",
        label: "Templates",
        Icon: UtensilsCrossed,
        route: DIET_ROUTES.TEMPLATES_LIST,
      },
      {
        id: "diet-catalog",
        label: "References",
        Icon: BookOpen,
        route: DIET_ROUTES.DIET_REFERENCE_DATA,
      },
    ],
  },
  {
    id: "fitness",
    label: "Fitness",
    items: [
      {
        id: "workout-exercises",
        label: "Exercises",
        Icon: Database,
        route: WORKOUT_ROUTES.EXERCISES_LIST,
      },
      {
        id: "workout-templates",
        label: "Templates",
        Icon: Dumbbell,
        route: WORKOUT_ROUTES.TEMPLATES_LIST,
        // Only the nav item is hidden: Nutritionists keep route access so a
        // client's Activity tab can still open the workout template picker.
        hiddenForDepartments: ["Nutritionist"],
      },
      {
        id: "workout-catalog",
        label: "References",
        Icon: BookOpen,
        route: WORKOUT_ROUTES.WORKOUT_REFERENCE_DATA,
      },
    ],
  },
  {
    id: "hr",
    label: "HR",
    items: [
      { id: "emp", label: "Employees", Icon: UserRoundCog, route: HR_ROUTES.EMPLOYEES },
      { id: "hr-references", label: "References", Icon: BookOpen, route: HR_ROUTES.REFERENCE_DATA },
    ],
  },
  {
    id: "business",
    label: "Business",
    items: [
      { id: "plan", label: "Pricing Plans", Icon: CreditCard, route: "/plan" },
      {
        id: "revenue",
        label: "Executive Analytics",
        Icon: TrendingUp,
        route: "/revenue",
      },
    ],
  },
];

const ALL_NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

function matchActiveNavId(pathname) {
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) {
    return 'user';
  }
  // /messages has no nav entry of its own any more — it is reached by popping
  // a thread out of the Users split view, so it highlights Users.
  if (pathname === '/messages' || pathname.startsWith('/messages/')) {
    return 'user';
  }
  let best = null;
  for (const link of ALL_NAV_ITEMS) {
    if (pathname === link.route || pathname.startsWith(`${link.route}/`)) {
      if (!best || link.route.length > best.route.length) best = link;
    }
  }
  return best?.id ?? null;
}

function NavItemButton({
  item,
  collapsed,
  indented,
  isActive,
  isNavigating,
  onNavigate,
  badgeCount = 0,
}) {
  const { Icon } = item;
  const badgeLabel = badgeCount > 99 ? "99+" : String(badgeCount);
  const iconClass = `w-[18px] h-[18px] flex-shrink-0 ${
    isActive ? "text-orange-600" : "text-gray-600"
  }`;

  return (
    <button
      type="button"
      onClick={() => onNavigate(item)}
      className={`w-full flex items-center rounded-lg transition-all duration-200 relative
        ${collapsed ? "justify-center px-2 py-2.5" : "gap-3 py-2.5"}
        ${!collapsed && (indented ? "pl-8 pr-3" : "px-3")}
        ${
          isActive
            ? "bg-orange-50 text-orange-600 font-medium shadow-sm border-l-2 border-orange-600"
            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-2 border-transparent"
        }`}
      title={collapsed ? item.label : undefined}
      aria-current={isActive ? "page" : undefined}
    >
      <span className="flex items-center justify-center w-5 h-5 flex-shrink-0 relative">
        {isNavigating ? (
          <Loader2 className={`${iconClass} animate-spin`} />
        ) : (
          <Icon className={iconClass} strokeWidth={isActive ? 2.25 : 2} />
        )}
        {/* Collapsed rail has no room for a pill, so show a corner dot instead. */}
        {collapsed && badgeCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-orange-primary"
          />
        )}
      </span>
      {!collapsed && (
        <span className="text-sm flex-1 min-w-0 text-left whitespace-nowrap overflow-hidden text-ellipsis">
          {item.label}
        </span>
      )}
      {!collapsed && badgeCount > 0 && (
        <span className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-orange-primary text-white text-[10px] font-semibold flex-shrink-0">
          {badgeLabel}
        </span>
      )}
    </button>
  );
}

function AccountMenuItem({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left ${
        danger
          ? "text-error-red hover:bg-error-red/10"
          : "text-gray-700 hover:bg-gray-50"
      }`}
      onClick={onClick}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {label}
    </button>
  );
}

export const AppSidebar = ({ collapsed = false, onToggleCollapse }) => {
  const { employeeData, departmentsWithRoles, logout, userProfileData } =
    useContext(ApiContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [lastActiveId, setLastActiveId] = useState(
    () => localStorage.getItem(LAST_ACTIVE_KEY) || "home"
  );
  const [navigatingId, setNavigatingId] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [failedPhotoUrl, setFailedPhotoUrl] = useState(null);
  const accountMenuRef = useRef(null);

  const railPad = collapsed ? "px-1.5" : "px-2 md:px-3";

  const { totalUnread, supportTotalUnread } = useRoomsUnread();
  const { isStaff, ensureProbe } = useOversightAccess();

  useEffect(() => {
    ensureProbe();
  }, [ensureProbe]);

  const visibleSections = useMemo(() => {
    // True when every one of the caller's departments is in `titles`, so someone
    // who is also in Fitness (or Executive) still sees the item.
    const fold = (title) => String(title).trim().toLowerCase();
    const callerDepartments = normalizeEmployeeAccess(employeeData).departments
      .map((dept) => fold(dept.title));
    const isOnlyIn = (titles) =>
      callerDepartments.length > 0 &&
      callerDepartments.every((dept) => titles.map(fold).includes(dept));

    const allow = (item) => {
      if (item.staffOnly && !isStaff) return false;
      if (item.hiddenForDepartments && isOnlyIn(item.hiddenForDepartments)) {
        return false;
      }
      return (
        !employeeData?.data ||
        hasRouteAccess(item.route, employeeData, departmentsWithRoles)
      );
    };

    return NAV_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(allow),
    })).filter((section) => section.items.length > 0);
  }, [employeeData, departmentsWithRoles, isStaff]);

  // Unread pill count per nav item: chat rooms for "Messages", assigned
  // support tickets for "Support" / "My Tickets".
  const badgeCountFor = (item) => {
    if (item.badge === "unread") return totalUnread;
    if (item.badge === "supportUnread") return supportTotalUnread;
    return 0;
  };

  const activeId = useMemo(() => matchActiveNavId(pathname), [pathname]);
  const highlightId = activeId || lastActiveId;

  useEffect(() => {
    if (!activeId) return;
    setLastActiveId(activeId);
    localStorage.setItem(LAST_ACTIVE_KEY, activeId);
    setNavigatingId((current) => (current === activeId ? null : current));
  }, [activeId]);

  useEffect(() => {
    if (!showAccountMenu) return undefined;
    const onPointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [showAccountMenu]);

  const profilePhotoUrl = useMemo(() => {
    const photos = userProfileData?.profile_photos;
    if (!Array.isArray(photos) || photos.length === 0) return null;

    const latest = photos.reduce((best, current) => {
      if (!best) return current;
      const bestTime = new Date(best.created_at || best.updated_at || 0);
      const currentTime = new Date(current.created_at || current.updated_at || 0);
      return currentTime > bestTime ? current : best;
    }, null);

    return latest?.url || latest?.blob_path || null;
  }, [userProfileData]);

  const profile = userProfileData?.profile;
  const employee = employeeData?.data;
  const displayName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    [employee?.first_name, employee?.last_name].filter(Boolean).join(" ") ||
    profile?.full_name ||
    employee?.full_name ||
    employee?.name ||
    "Profile";

  const handleNavigate = (item) => {
    setLastActiveId(item.id);
    setNavigatingId(item.id);
    setShowAccountMenu(false);
    navigate(item.route);
  };

  const closeAccountMenu = () => setShowAccountMenu(false);

  const renderNavItem = (item, indented = false) => (
    <NavItemButton
      key={item.id}
      item={item}
      collapsed={collapsed}
      indented={indented}
      isActive={highlightId === item.id}
      isNavigating={navigatingId === item.id}
      onNavigate={handleNavigate}
      badgeCount={badgeCountFor(item)}
    />
  );

  return (
    <>
      <aside className="h-full w-full bg-white border-r border-gray-200 flex flex-col shadow-sm">
        <div
          className={`border-b border-gray-100 flex items-center gap-2 ${
            collapsed
              ? "px-2 py-3 justify-center flex-col"
              : "px-3 py-3 md:px-4 justify-between"
          }`}
        >
          <div
            className={`flex items-center gap-2 min-w-0 ${
              collapsed ? "justify-center" : "flex-1"
            }`}
          >
            <img
              className="h-8 w-8 object-contain flex-shrink-0"
              src={flexlogo}
              alt="Flex Board Logo"
            />
            {!collapsed && (
              <span className="font-bold text-lg text-gray-800 tracking-tight truncate">
                Flex Board
              </span>
            )}
          </div>
          {/* The collapsed rail is too narrow for two buttons side by side. */}
          <div
            className={`flex items-center gap-1 flex-shrink-0 ${
              collapsed ? "flex-col" : ""
            }`}
          >
            <NotificationBell collapsed={collapsed} />
            <button
              type="button"
              onClick={onToggleCollapse}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-primary"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <PanelLeft className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        <nav
          className={`flex-1 py-3 overflow-y-auto overflow-x-hidden ${railPad} ${
            collapsed ? "space-y-1" : "space-y-4"
          }`}
        >
          {collapsed
            ? visibleSections.flatMap((section) =>
                section.items.map((item) => renderNavItem(item))
              )
            : visibleSections.map((section) => (
                <div key={section.id} className="space-y-1">
                  {section.label && (
                    <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                      {section.label}
                    </div>
                  )}
                  {section.items.map((item) =>
                    renderNavItem(item, Boolean(section.label))
                  )}
                </div>
              ))}
        </nav>

        <div className={`border-t border-gray-100 relative ${railPad} py-3`} ref={accountMenuRef}>
          <button
            type="button"
            className={`w-full flex items-center rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 relative
              ${collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"}`}
            aria-label="Account menu"
            aria-expanded={showAccountMenu}
            aria-haspopup="menu"
            onClick={() => setShowAccountMenu((open) => !open)}
            title={collapsed ? displayName : undefined}
          >
            {profilePhotoUrl && failedPhotoUrl !== profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt=""
                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                onError={() => setFailedPhotoUrl(profilePhotoUrl)}
              />
            ) : (
              <UserRound
                size={18}
                className="text-orange-primary flex-shrink-0"
              />
            )}
            {!collapsed && (
              <>
                <span className="text-sm flex-1 min-w-0 text-left whitespace-nowrap overflow-hidden text-ellipsis">
                  {displayName}
                </span>
                <ChevronUp
                  className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                    showAccountMenu ? "" : "rotate-180"
                  }`}
                />
              </>
            )}
          </button>

          {showAccountMenu && (
            <div
              role="menu"
              className="absolute z-50 left-2 right-2 bottom-full mb-2 py-1.5 bg-white border border-gray-200 rounded-lg shadow-lg"
            >
              <AccountMenuItem
                icon={UserRound}
                label="Profile"
                onClick={() => {
                  closeAccountMenu();
                  setShowProfile(true);
                }}
              />
              <AccountMenuItem
                icon={LogOut}
                label="Logout"
                danger
                onClick={() => {
                  closeAccountMenu();
                  logout();
                  navigate("/login");
                }}
              />
            </div>
          )}
        </div>
      </aside>

      {showProfile && (
        <div
          className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setShowProfile(false)}
        >
          {/* ProfileCard draws the 4a card (rail + tabs + its own close). */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Your profile"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[920px] h-[min(620px,90vh)] bg-white rounded-2xl shadow-[0_24px_60px_rgba(31,35,40,0.18)] overflow-hidden"
          >
            <ProfileCard
              onClose={() => setShowProfile(false)}
              showProfilePicture
              showPhotoControls
            />
          </div>
        </div>
      )}
    </>
  );
};
